// src/features/dashboard/components/KPISection.tsx

import { useNavigate } from 'react-router-dom';

import { useKpi } from '../hooks/useKpi';

import { useLoaderContext } from '../../../components/Loader/LoaderContext';

type KPISectionProps = {
  ManagerEmployeeCode: string;
};

const KPISection = ({
  ManagerEmployeeCode,
}: KPISectionProps) => {
  const navigate = useNavigate();

  const { showLoader } =
    useLoaderContext();

  const {
    kpiCards,
    loading,
    error,
  } = useKpi(
    ManagerEmployeeCode,
  );

  /* ============================================================
     LOADING
  ============================================================ */

  if (loading) {
    return (
      <div
        className="
          w-full
          rounded-[18px]
          border
          border-[#E5E7EB]
          bg-white
          p-5
          text-[14px]
          font-medium
          text-[#667085]
        "
      >
        Loading KPI data...
      </div>
    );
  }

  /* ============================================================
     ERROR
  ============================================================ */

  if (error) {
    return (
      <div
        className="
          w-full
          rounded-[18px]
          border
          border-[#FECACA]
          bg-[#FEF2F2]
          p-5
        "
      >
        <h3
          className="
            text-[14px]
            font-semibold
            text-[#DC2626]
          "
        >
          KPI Section Error
        </h3>

        <p
          className="
            mt-1
            text-[12px]
            text-[#7F1D1D]
          "
        >
          {error}
        </p>
      </div>
    );
  }

  /* ============================================================
     EMPTY STATE
  ============================================================ */

  if (
    !kpiCards ||
    kpiCards.length === 0
  ) {
    return null;
  }

  /* ============================================================
     DYNAMIC GRID
     
     5 cards -> 5 columns
     4 cards -> 4 columns
     3 cards -> 3 columns
     2 cards -> 2 columns
     1 card  -> 1 column

     At smaller screens:
     Mobile -> 1
     SM     -> 2
     MD     -> 3

     At LG and above the number of cards controls
     the number of columns.
  ============================================================ */

  const desktopColumnCount =
    Math.min(
      Math.max(
        kpiCards.length,
        1,
      ),
      5,
    );

  /*
   * Tailwind needs complete class names at build time,
   * therefore map the values explicitly.
   */
  const desktopGridClass =
    desktopColumnCount === 5
      ? 'lg:grid-cols-5'
      : desktopColumnCount === 4
        ? 'lg:grid-cols-4'
        : desktopColumnCount === 3
          ? 'lg:grid-cols-3'
          : desktopColumnCount === 2
            ? 'lg:grid-cols-2'
            : 'lg:grid-cols-1';

  /* ============================================================
     KPI SECTION
  ============================================================ */

  return (
    <div className="w-full">

      {/* ========================================================
          RESPONSIVE KPI GRID

          Mobile:
          1 column

          Small:
          2 columns

          Medium:
          3 columns

          Large:
          Dynamic based on KPI count

          Example:
          5 cards -> 5 in same row
          4 cards -> 4 in same row
          3 cards -> 3 in same row
      ======================================================== */}

      <div
        className={`
          grid
          w-full
          grid-cols-1
          gap-4

          sm:grid-cols-2

          md:grid-cols-3

          ${desktopGridClass}
        `}
      >

        {kpiCards.map((card) => {
          const Icon = card.icon;

          /* ======================================================
             CARD CLICK
          ====================================================== */

          const handleClick = () => {
            if (!card.route) {
              return;
            }

            showLoader();

            navigate(
              card.route,
            );
          };

          return (
            <div
              key={card.title}
              onClick={handleClick}
              className={`
                group
                relative
                box-border
                h-[108px]
                w-full
                min-w-0
                overflow-hidden

                rounded-[18px]

                border
                border-[#DDD6FE]

                bg-white

                px-[14px]
                py-[12px]

                shadow-[0_1px_3px_rgba(0,0,0,0.06)]

                transition-all
                duration-300
                ease-in-out

                ${
                  card.route
                    ? 'cursor-pointer'
                    : ''
                }

                hover:-translate-y-[4px]

                hover:shadow-[0_10px_22px_rgba(0,0,0,0.10)]

                ${card.hoverBg}

                ${card.borderColor}
              `}
            >

              {/* ==================================================
                  TOP ROW
              ================================================== */}

              <div
                className="
                  flex
                  min-w-0
                  items-center
                  justify-between
                  gap-[8px]
                "
              >

                {/* ==================================================
                    ICON + TITLE
                ================================================== */}

                <div
                  className="
                    flex
                    min-w-0
                    items-center
                    gap-[9px]
                  "
                >

                  {/* Icon */}

                  <div
                    className={`
                      flex
                      h-[30px]
                      w-[30px]
                      shrink-0

                      items-center
                      justify-center

                      rounded-full

                      ${card.iconBg}

                      transition-all
                      duration-300
                      ease-in-out

                      group-hover:scale-105
                      group-hover:rotate-3
                    `}
                  >
                    <Icon
                      className="
                        h-[16px]
                        w-[16px]
                        text-white
                      "
                    />
                  </div>

                  {/* Title */}

                  <span
                    className="
                      min-w-0
                      truncate

                      text-[12px]
                      font-semibold
                      leading-[16px]

                      text-[#101010]

                      transition-all
                      duration-300
                      ease-in-out

                      group-hover:translate-x-[1px]
                    "
                    title={card.title}
                  >
                    {card.title}
                  </span>

                </div>

                {/* ==================================================
                    CLICK INDICATOR
                ================================================== */}

                {card.route && (
                  <span
                    className="
                      flex
                      h-[18px]
                      w-[18px]
                      shrink-0

                      items-center
                      justify-center

                      text-[10px]
                      leading-none

                      opacity-60

                      transition-all
                      duration-300

                      group-hover:scale-110
                      group-hover:opacity-100
                    "
                    aria-hidden="true"
                  >
                    👆
                  </span>
                )}

              </div>

              {/* ==================================================
                  VALUE ROW
              ================================================== */}

              <div
                className="
                  mt-[15px]

                  flex
                  min-w-0
                  items-baseline

                  gap-[4px]

                  leading-none
                "
              >

                {/* Main value */}

                <span
                  className="
                    shrink-0

                    text-[18px]
                    font-bold
                    leading-[20px]

                    tracking-[-0.2px]

                    text-[#050505]

                    transition-all
                    duration-300
                    ease-in-out

                    group-hover:translate-y-[-1px]
                  "
                >
                  {card.value}
                </span>

                {/* Subtitle */}

                {card.subtitle && (
                  <span
                    className="
                      min-w-0
                      truncate

                      text-[11px]
                      font-normal
                      leading-[16px]

                      text-[#78716C]
                    "
                    title={card.subtitle}
                  >
                    {card.subtitle}
                  </span>
                )}

              </div>

              {/* ==================================================
                  FOOTER
              ================================================== */}

              {card.footer && (
                <div
                  className={`
                    absolute

                    bottom-[11px]

                    left-[14px]
                    right-[14px]

                    truncate

                    text-[11px]
                    font-normal
                    leading-[14px]

                    transition-all
                    duration-300
                    ease-in-out

                    group-hover:translate-x-[1px]

                    ${card.footerColor}
                  `}
                  title={card.footer}
                >
                  {card.footer}
                </div>
              )}

            </div>
          );
        })}

      </div>

    </div>
  );
};

export default KPISection;